import { forwardRef, useCallback } from 'react';
import { Table } from 'antd';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { CALENDAR_TABLE_HEIGHT_OFFSET } from '../../config/calendarConstants';
import { CHANGE_TYPE_CONFIG } from '../../config/changeTypeConfig';
import type { CalendarRow, PromoTypeLegendItem } from '../../types/calendar';
import PromoTypeLegend from './components/PromoTypeLegend';

export interface CalendarTableProps {
    dataSource: CalendarRow[];
    columns: any[];
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    expandedRowKeys: string[];
    setExpandedRowKeys: (keys: string[]) => void;
    handleTableChange: any;
    legend: PromoTypeLegendItem[];
    showChanges: boolean;
}

const CalendarTable = forwardRef<HTMLDivElement, CalendarTableProps>(
    (
        {
            dataSource,
            columns,
            isPending,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
            expandedRowKeys,
            setExpandedRowKeys,
            handleTableChange,
            legend,
            showChanges,
        },
        ref,
    ) => {
        const getRowClassName = useCallback(
            (record: CalendarRow) => {
                if (!showChanges || record.children || !record.promo_lifecycle_status) return '';
                return CHANGE_TYPE_CONFIG[record.promo_lifecycle_status]?.rowClassName ?? '';
            },
            [showChanges],
        );

        return (
            <div className="h-full" ref={ref}>
                <InfiniteScroller
                    data={dataSource}
                    onNext={fetchNextPage}
                    hasMore={hasNextPage}
                    loading={isFetchingNextPage}
                    height={window.innerHeight - CALENDAR_TABLE_HEIGHT_OFFSET}
                >
                    <Table
                        className="planner-calendar-table"
                        pagination={false}
                        dataSource={dataSource}
                        columns={columns}
                        scroll={{ x: 'max-content' }}
                        loading={isPending || isFetching || isFetchingNextPage}
                        sticky={{
                            getContainer: () =>
                                document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                        }}
                        bordered
                        onChange={handleTableChange}
                        rowClassName={getRowClassName}
                        expandable={{
                            expandedRowKeys,
                            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                            showExpandColumn: false,
                        }}
                    />
                </InfiniteScroller>
                <PromoTypeLegend items={legend} />
            </div>
        );
    },
);

CalendarTable.displayName = 'CalendarTable';
export default CalendarTable;
